import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { api } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { Customer } from '../../types/customer';
import { UsersPage } from '../Users/UsersPage';
import { canManageCrossCustomerUsers } from '../../utils/ownerCustomerAccess';

export const AdminUsersPage: React.FC = () => {
  const { user } = useAuth();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');

  const canSelectCustomer = canManageCrossCustomerUsers(user);
  const currentCustomerId = sessionStorage.getItem('@KnowInDoc:customer_id') || '';

  useEffect(() => {
    if (!canSelectCustomer)
    {
      setSelectedCustomerId(currentCustomerId);
      return;
    }

    const loadCustomers = async () => {
      setCustomersLoading(true);
      try
      {
        const response = await api.get<Customer[]>('/customers');
        const activeCustomers = (response.data || []).filter(customer => customer.isActive !== false);
        setCustomers(activeCustomers);

        if (currentCustomerId)
        {
          setSelectedCustomerId(currentCustomerId);
        }
        else if (activeCustomers.length > 0)
        {
          setSelectedCustomerId(activeCustomers[0].id);
        }
      }
      catch (error)
      {
        console.error('[AdminUsersPage] Erro ao carregar clientes:', error);
      }
      finally
      {
        setCustomersLoading(false);
      }
    };

    loadCustomers();
  }, [canSelectCustomer, currentCustomerId]);

  const filteredCustomers = useMemo(() => {
    const term = customerSearch.trim().toLowerCase();
    if (!term)
    {
      return customers;
    }

    return customers.filter(customer =>
      customer.name.toLowerCase().includes(term) ||
      customer.slug.toLowerCase().includes(term) ||
      (customer.domain || '').toLowerCase().includes(term)
    );
  }, [customers, customerSearch]);

  const selectedCustomer = useMemo(
    () => customers.find(customer => customer.id === selectedCustomerId),
    [customers, selectedCustomerId]
  );

  const isManagingOtherCustomer = Boolean(
    canSelectCustomer &&
    selectedCustomerId &&
    currentCustomerId &&
    selectedCustomerId !== currentCustomerId
  );

  if (!user?.isAdmin)
  {
    return <Navigate to="/admin/control-panel" replace />;
  }

  return (
    <div className="space-y-4">
      {canSelectCustomer && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cliente
              </label>
              <input
                type="text"
                placeholder="Buscar cliente por nome, slug ou domínio..."
                className="mb-2 w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={customerSearch}
                onChange={event => setCustomerSearch(event.target.value)}
              />
              <select
                className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={selectedCustomerId}
                onChange={event => setSelectedCustomerId(event.target.value)}
                disabled={customersLoading || filteredCustomers.length === 0}
              >
                {filteredCustomers.length === 0 ? (
                  <option value="">Nenhum cliente encontrado</option>
                ) : (
                  filteredCustomers.map(customer => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name}
                      {customer.isOwner ? ' (Owner)' : ''}
                    </option>
                  ))
                )}
              </select>
            </div>

            {isManagingOtherCustomer && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 lg:max-w-md">
                Você está configurando usuários de outro cliente. Ideal para implantações e onboarding.
              </div>
            )}
          </div>
        </div>
      )}

      {selectedCustomerId ? (
        <UsersPage
          key={selectedCustomerId}
          targetCustomerId={selectedCustomerId}
          targetCustomerIsOwner={selectedCustomer?.isOwner === true}
          pageTitle="Usuários"
          showImport={!isManagingOtherCustomer}
          showTeamManagement={!isManagingOtherCustomer}
          showCustomerContext={canSelectCustomer}
          customerName={selectedCustomer?.name}
        />
      ) : (
        <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-600">
          {customersLoading ? 'Carregando clientes...' : 'Selecione um cliente para gerenciar usuários.'}
        </div>
      )}
    </div>
  );
};
